<template>
  <div>
    <form
      id="gf-survey"
      class="thank-you-message-container main-container"
      v-if="survey || error"
    >
      <div v-if="error" class="error-page-container main-container">
        <survey-header
          :title="survey ? survey.title : 'Survey'"
          :description="survey ? survey.description : ''"
          :title-color="headerColorOptions.baseColor"
          :description-color="headerColorOptions.secondaryColor"
          :title-font-size="headerColorOptions.titleFontSize"
          :description-font-size="headerColorOptions.descriptionFontSize"
          :highlight-color="colorOptions.highlightColor"
          :img-link="survey && survey.logo ? survey.logo : null"
        ></survey-header>

        <survey-error-page
          :title-color="headerColorOptions.baseColor"
          :description-color="headerColorOptions.secondaryColor"
          :highlight-color="colorOptions.highlightColor"
          :title-font-size="headerColorOptions.titleFontSize"
          :error-title="errorContent.title"
          :error-message="errorContent.message"
          :error-details="errorContent.details"
        ></survey-error-page>
      </div>
      <div class="row">
        <div
          class="survey-container main-container"
          :style="backgroundColor"
          v-if="
            loadedSurvey &&
            !error &&
            (surveyStatus === 'ready' || surveyStatus === 'nps_finished')
          "
        >
          <div class="survey-inner">
            <survey-header
              :title="survey.title"
              :description="survey.description"
              :title-color="survey.options.baseColor"
              :description-color="survey.options.secondaryColor"
              :title-font-size="survey.options.titleFontSize"
              :description-font-size="survey.options.descriptionFontSize"
              :highlight-color="colorOptions.highlightColor"
              :img-link="survey.logo"
            ></survey-header>
            <div class="settings">
              <language-selector
                :locales="survey.locales"
                :lang="locale"
                :color="survey.options.baseColor"
              />
            </div>
            <!-- Page title and indicator at the top -->
            <div v-if="survey.paginationEnabled" class="pagination-header">
              <div
                class="page-title"
                v-if="pageTitle"
                :style="{ color: colorOptions.baseColor }"
              >
                {{ pageTitle }}
              </div>
              <div
                class="page-indicator"
                :style="{ color: colorOptions.secondaryColor }"
              >
                Page {{ currentPage }} of {{ totalPages }}
              </div>
            </div>
            <div class="content">
              <div
                class="question-container"
                v-for="field in surveyFields"
                v-bind:key="field.id"
              >
                <field-selector
                  :fields="surveyFields"
                  :outOfFive="survey.options.outOfFive && !field.isNps"
                  :field="field"
                  :colors="colorOptions"
                  :shape="survey.options.shape"
                  @click.native="clickEvent($event, field)"
                ></field-selector>
              </div>
              <div
                class="guest-details"
                v-if="
                  isGeneric &&
                  loadedSurvey &&
                  !pms_external_id &&
                  (!survey.paginationEnabled || isLastPage)
                "
              >
                <generic-form
                  :infoFields="survey.infoFields"
                  :lang="locale"
                  :error="guestDetailsError"
                  :apiErrors="guestApiErrors"
                  :colorOptions="colorOptions"
                  :shapeClass="shapeClass"
                  @details-updated="updateGuestDetails"
                />
              </div>
              <div class="button-wrapper">
                <!-- Pagination Navigation -->
                <div
                  v-if="survey.paginationEnabled"
                  class="pagination-controls"
                >
                  <button
                    v-if="currentPage > 1"
                    type="button"
                    :class="['pagination-button', 'back-button', shapeClass]"
                    :style="{
                      color: colorOptions.highlightColor,
                      borderColor: colorOptions.highlightColor
                    }"
                    @click.prevent="goToPreviousPage($event)"
                  >
                    Back
                  </button>
                  <button
                    v-if="!isLastPage"
                    type="button"
                    :class="['pagination-button', 'next-button', shapeClass]"
                    :style="{ backgroundColor: colorOptions.highlightColor }"
                    @click.prevent="goToNextPage($event)"
                  >
                    Next
                  </button>
                  <button
                    v-if="isLastPage"
                    type="button"
                    :style="submitButtonStyle"
                    :disabled="isSubmitting"
                    :class="['submit-button', shapeClass]"
                    v-on:click="submitSurvey"
                  >
                    {{ survey.options.submitText
                    }}{{ isSubmitting ? '...' : '' }}
                  </button>
                </div>
                <!-- Regular Submit Button for non-paginated surveys -->
                <button
                  v-if="!survey.paginationEnabled"
                  type="button"
                  :style="submitButtonStyle"
                  :disabled="isSubmitting"
                  :class="['submit-button', shapeClass]"
                  v-on:click="submitSurvey"
                >
                  {{ survey.options.submitText }}{{ isSubmitting ? '...' : '' }}
                </button>
              </div>
            </div>
            <div class="bottom">
              {{ survey.options.bottomText }}
            </div>
          </div>
        </div>
        <div
          class="thank-you-message-container main-container"
          v-if="loadedSurvey && !error && surveyStatus === 'finished'"
        >
          <survey-header
            :title="survey.title"
            :description="survey.description"
            :title-color="survey.options.baseColor"
            :description-color="survey.options.secondaryColor"
            :title-font-size="survey.options.titleFontSize"
            :description-font-size="survey.options.descriptionFontSize"
            :highlight-color="colorOptions.highlightColor"
            :img-link="survey.logo"
          ></survey-header>
          <thank-you-page
            :title="survey.title"
            :description="survey.description"
            :title-color="colorOptions.baseColor"
            :description-color="colorOptions.secondaryColor"
            :highlight-color="colorOptions.highlightColor"
            :title-font-size="survey.options.titleFontSize"
            :description-font-size="survey.options.descriptionFontSize"
            :thank-you-text="survey.options.thankYouText"
            :subscribe="survey.subscribe"
          ></thank-you-page>
        </div>
        <!-- Side image - show default image when there's an error -->
        <div
          v-if="loadedSurvey && survey && survey.mainImage"
          class="side-image"
          v-bind:style="{
            'background-image': survey.mainImage
              ? 'url(' + survey.mainImage + ')'
              : 'none',
            height: '100%'
          }"
        ></div>
        <div
          v-else-if="error"
          class="side-image"
          v-bind:style="{
            'background-image':
              'url(https://images.unsplash.com/photo-1492691704634-0fe43e322381?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=crop&w=726&q=80)',
            height: '100%'
          }"
        ></div>
      </div>
    </form>
  </div>
</template>

<script>
/* eslint-disable */
import Vue from 'vue'
import fields from 'survey-fields'
import SurveyActionsMixin from '../mixins/SurveyActionsMixin'
import TrackingService from '../services/TrackingService'
import GenericForm from './GenericForm'
import LanguageSelector from './LanguageSelector'
import SurveyErrorPage from './SurveyErrorPage'
import vSelect from 'vue-select'
import 'vue-select/dist/vue-select.css'

Vue.component('v-select', vSelect)

Vue.use(fields)

export default {
  name: 'Survey',
  mixins: [SurveyActionsMixin],
  data() {
    return {
      // Ensure survey object always has a valid structure to prevent template errors
      survey: {
        title: '',
        description: '',
        mainImage: null,
        options: {
          baseColor: '#2c2d4a',
          secondaryColor: '#90919e',
          highlightColor: '#00aef1',
          backgroundColor: '#ffffff',
          shape: 'rounded',
          submitText: 'Submit',
          bottomText: '',
          titleFontSize: 24,
          descriptionFontSize: 16
        },
        fields: [],
        paginationEnabled: false
      }
    }
  },
  components: {
    GenericForm,
    LanguageSelector,
    SurveyErrorPage
  },
  computed: {
    availableFields() {
      if (this.surveyStatus === 'ready') {
        return this.survey.fields
      }

      if (this.surveyStatus === 'nps_finished') {
        return this.survey.fields.filter(field => !field.isNps)
      }
    },
    surveyFields() {
      let fields = this.availableFields
      // If pagination is enabled, filter fields based on current page
      if (this.survey.paginationEnabled) {
        fields = fields.filter(field => field.pageNumber === this.currentPage)
      }

      return this.sortFields(fields)
    },
    totalPages() {
      if (!this.survey || !this.survey.paginationEnabled) return 1
      return Object.keys(this.survey.pageTitles).length
    },
    isLastPage() {
      return this.currentPage >= this.totalPages
    },
    pageTitle() {
      if (
        !this.survey ||
        !this.survey.paginationEnabled ||
        !this.survey.pageTitles
      ) {
        return null
      }

      // Get the page title for the current page from pageTitles
      return this.survey.pageTitles[this.currentPage]
    },
    submitButtonStyle() {
      return {
        backgroundColor: this.colorOptions.highlightColor
      }
    },
    shapeClass() {
      const shape =
        this.survey && this.survey.options ? this.survey.options.shape : null
      return shape ? `shape-${shape}` : 'shape-rounded'
    },
    colorOptions() {
      let colorSettings = {
        baseColor: this.survey.options.baseColor,
        secondaryColor: this.survey.options.secondaryColor,
        highlightColor: this.survey.options.highlightColor
      }

      if (!colorSettings.baseColor) {
        colorSettings.baseColor = '#2c2d4a'
      }
      if (!colorSettings.secondaryColor) {
        colorSettings.secondaryColor = '#90919e'
      }
      if (!colorSettings.highlightColor) {
        colorSettings.highlightColor = '#00aef1'
      }

      return colorSettings
    },
    backgroundColor() {
      const options =
        this.survey && this.survey.options ? this.survey.options : {}
      return {
        backgroundColor: options.backgroundColor || '#ffffff'
      }
    },
    headerColorOptions() {
      return {
        baseColor:
          this.survey && this.survey.options
            ? this.survey.options.baseColor
            : '#2c2d4a',
        secondaryColor:
          this.survey && this.survey.options
            ? this.survey.options.secondaryColor
            : '#90919e',
        titleFontSize:
          this.survey && this.survey.options
            ? this.survey.options.titleFontSize
            : 24,
        descriptionFontSize:
          this.survey && this.survey.options
            ? this.survey.options.descriptionFontSize
            : 16
      }
    },
    errorContent() {
      // Token expired error
      if (this.error && this.error.code === 'token_expired') {
        return {
          title: 'Link Expired',
          message: 'This survey link has expired and is no longer active.',
          details: this.error.message
        }
      }

      // Invalid token error
      if (this.error && this.error.code === 'invalid_token') {
        return {
          title: 'Invalid Survey Link',
          message:
            'The survey link you are using is invalid or has been tampered with.',
          details: this.error.message || ''
        }
      }

      // Not found error
      if (this.error === 'Not found') {
        return {
          title: 'Survey Not Available',
          message:
            'This survey may be paused, public access may have been revoked, or it is no longer available.',
          details: ''
        }
      }

      // Not available error
      if (this.error === 'Not available') {
        return {
          title: 'Survey Unavailable',
          message:
            'This survey is currently not available for submissions. Public access may have been disabled by the administrator.',
          details: ''
        }
      }

      // Default error
      return {
        title: 'Survey Access Restricted',
        message: "This survey is not public or doesn't exist anymore.",
        details:
          typeof this.error === 'object' && this.error.message
            ? this.error.message
            : ''
      }
    }
  },
  methods: {
    goToNextPage() {
      // Validate current page fields before proceeding
      const currentPageFields = this.survey.fields.filter(
        field => field.pageNumber === this.currentPage
      )
      const answers = this.pickAnswersPerField(currentPageFields)
      const invalidAnswers = this.getInvalidAnswers(answers)

      if (invalidAnswers.length) {
        Vue.nextTick(() => {
          const errorElement = document.getElementsByClassName('error')[0]
          if (errorElement) {
            errorElement.parentElement.parentElement.parentElement.parentElement.scrollIntoView(
              { behavior: 'smooth' }
            )
          }
        })
        return
      }

      // Proceed to next page
      if (this.currentPage < this.totalPages) {
        this.currentPage++
        // Scroll to top when changing pages
        window.scrollTo(0, 0)
      }
    },
    goToPreviousPage() {
      if (this.currentPage > 1) {
        this.currentPage--
        // Scroll to top when changing pages
        window.scrollTo(0, 0)
      }
    },
    sortFields(fields) {
      return fields.map(field => {
        if (
          (field.type === 'checkbox' || field.type === 'radiobutton') &&
          Array.isArray(field.answerOptions)
        ) {
          field.answerOptions = field.answerOptions
            .filter(option => option)
            .sort((a, b) => (a.order || 0) - (b.order || 0)) // Sort by order, defaulting to 0
        }
        return field
      })
    },
    clickEvent(event, field) {
      this.analytics.event('clickedField', {
        field: {
          id: field.id,
          title: field.title
        },
        value: event.target.innerText
      })
    },
    parseJwt(token) {
      var base64Url = token.split('.')[1]
      var base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
      var jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(function (c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
          })
          .join('')
      )

      return JSON.parse(jsonPayload)
    },
    /**
     * To fill nps field pass id={fieldId} and value={fieldValue} to the url query string
     *
     * @param npsFieldId
     * @param npsFieldValue
     */
    fillSurveyNpsField(npsFieldValue) {
      if (isNaN(npsFieldValue) || npsFieldValue < 1 || npsFieldValue > 10)
        return false
      const index = this.survey.fields.findIndex(field => {
        return field.isNps
      })
      if (index < 0) {
        return false
      }
      const canBeFilled =
        this.survey.fields[index].type === 'radiobutton' ||
        this.survey.fields[index].type === 'checkbox' ||
        this.survey.fields[index].type === 'range'
      if (canBeFilled) {
        const optionIndex = this.survey.fields[index].answerOptions.findIndex(
          option => {
            return parseInt(option.value) === parseInt(npsFieldValue)
          }
        )
        Vue.set(
          this.survey.fields[index].answerOptions[optionIndex],
          'selected',
          true
        )
      }
    },
    isTriggerFieldVisible(field, fields) {
      if (!field.triggers || field.triggers.length === 0) {
        return true
      }

      // we should cache this to optimize performance
      // this is a hack because computed variables acted weird
      let allSelected = []

      for (let f of fields) {
        if (f.answerOptions) {
          const selectedAnswers = f.answerOptions.filter(answer => {
            return answer.selected === true
          })

          for (let ans of selectedAnswers) {
            allSelected.push(ans.uuid)
          }
        }
      }
      for (let trigger of field.triggers) {
        if (allSelected.includes(trigger.uuid)) {
          return true
        }
      }

      return false
    },
    pickAnswersPerField(fields) {
      let instance = this
      const answers = fields.map(field => {
        if (
          field.type === 'text' ||
          field.type === 'string' ||
          field.type === 'number'
        ) {
          let isVisible = instance.isTriggerFieldVisible(field, fields)
          let isRequired = field.required && isVisible
          return {
            required: isRequired,
            fieldId: field.id,
            value: field.value
          }
        } else {
          const selectedAnswers = field.answerOptions.filter(answer => {
            return answer.selected === true
          })

          let isVisible = instance.isTriggerFieldVisible(field, fields)
          let isRequired = field.required && isVisible

          return {
            required: isRequired,
            fieldId: field.id,
            selectedAnswersIds: selectedAnswers.map(answer => answer.id)
          }
        }
      })
      return answers
    },
    getInvalidAnswers(answers) {
      const invalidAnswers = answers.filter(answer => {
        const hasAnswer =
          answer.value != null ||
          (answer.selectedAnswersIds && answer.selectedAnswersIds.length)
        return answer.required && !hasAnswer
      })

      this.survey.fields = this.survey.fields.map(field => {
        const fieldIsInvalid = invalidAnswers.find(
          answer => answer.fieldId === field.id
        )
        return fieldIsInvalid
          ? Object.assign({}, field, { invalid: true })
          : Object.assign({}, field, { invalid: false })
      })

      return invalidAnswers
    },
    guestErrors(guest) {
      if (
        guest.firstName.required == true &&
        guest.firstName.value.length < 2
      ) {
        return 'First name cannot be empty.'
      }
      if (guest.lastName.required == true && guest.lastName.value.length < 2) {
        return 'Last name cannot be empty.'
      }
      if (guest.email.required == true && guest.email.value.length < 2) {
        return 'Email cannot be empty.'
      }
      if (
        guest.roomNumber.required == true &&
        guest.roomNumber.value.length < 1
      ) {
        return 'Room number cannot be empty.'
      }

      if (
        guest.nationalityCode.required == true &&
        guest.nationalityCode.value.length < 1
      ) {
        return 'Please choose your country.'
      }

      if (
        guest.checkInDate.required == true &&
        guest.checkInDate.value.length < 1
      ) {
        return 'Please choose your check-in date.'
      }
      if (
        guest.checkOutDate.required == true &&
        guest.checkOutDate.value.length < 1
      ) {
        return 'Please choose your check-out date.'
      }
      if (guest.checkOutDate < guest.checkInDate) {
        return 'The check-out date cannot be earlier than the check-in date.'
      }
      return false
    },
    submitSurvey() {
      this.isSubmitting = true
      this.guestApiErrors = null
      if (!this.isGeneric && this.token) {
        const jwt = this.parseJwt(this.token)
        if (jwt.user.preview === true) return
      }
      const answers = this.pickAnswersPerField(this.availableFields)
      const invalidAnswers = this.getInvalidAnswers(answers)
      this.formSubmitted = true

      if (invalidAnswers.length) {
        Vue.nextTick(() => {
          const errorElement = document.getElementsByClassName('error')[0]
          const scrollTarget =
            errorElement &&
            errorElement.parentElement &&
            errorElement.parentElement.parentElement &&
            errorElement.parentElement.parentElement.parentElement &&
            errorElement.parentElement.parentElement.parentElement.parentElement

          if (scrollTarget && scrollTarget.scrollIntoView) {
            scrollTarget.scrollIntoView({ behavior: 'smooth' })
          }
          invalidAnswers.forEach(answer => {
            this.analytics.event('failedValidation', {
              field: { id: answer.fieldId }
            })
          })
        })
        this.isSubmitting = false
        this.guestApiErrors = null
      }

      let error = false

      if (Object.keys(this.guest).length > 0) {
        error = this.guestErrors(this.guest)
      }

      if (this.isGeneric && error) {
        this.guestDetailsError = error
        this.guestApiErrors = null
        this.isSubmitting = false
        return
      } else {
        this.guestDetailsError = ''
      }

      const answersToPost = answers.map(answer => {
        delete answer.required
        return answer
      })

      if (!invalidAnswers.length && this.submitRequest) {
        this.analytics.event('surveySubmitted', {})

        this.submitRequest(answersToPost)
          .then(res => {
            this.guestApiErrors = null
            this.performActions(
              'after',
              this.survey.actions,
              res.applicableActions
            )
            this.isSubmitting = false
          })
          .catch(error => {
            const apiErrors =
              error &&
              error.response &&
              error.response.data &&
              error.response.data.error

            if (apiErrors) {
              this.guestApiErrors = apiErrors
              this.guestDetailsError = ''
              this.isSubmitting = false
              return
            }

            this.guestApiErrors = null
            this.isSubmitting = false
          })
      }
    },
    updateGuestDetails(guest) {
      this.guest = guest
      this.guestApiErrors = null
    }
  },
  mounted() {
    this.slug = this.$route.params.slug
    this.token = this.$route.params.jwt
    this.pms_external_id = this.$route.query.token
    this.anonymousGuest = this.$route.query.g ? this.$route.query.g : null

    if (this.token) {
      const user = this.parseJwt(this.token).user
      if (user.preview !== true) {
        this.isGeneric = true
      }
      this.analytics = new TrackingService(this.token)
    } else if (this.anonymousGuest) {
      this.analytics = {
        event: val => {
          /* */
        },
        trackScrolling: val => {
          /* */
        }
      }
    } else {
      this.isGeneric = true
      this.analytics = {
        event: val => {
          /* */
        },
        trackScrolling: val => {
          /* */
        }
      }
    }

    this.locale = this.$route.query.locale ? this.$route.query.locale : 'eng'
    if (this.getSurvey) {
      this.getSurvey(
        this.slug,
        this.token,
        this.locale,
        this.anonymousGuest
      ).catch(error => {
        // Handle all error responses
        if (error.response && error.response.data) {
          this.error = error.response.data
          this.loadedSurvey = true

          // Create a minimal survey object for all errors to avoid template errors
          this.survey = {
            title: '',
            description: '',
            options: {
              baseColor: '#2c2d4a',
              secondaryColor: '#90919e',
              highlightColor: '#00aef1',
              backgroundColor: '#ffffff'
            },
            mainImage: null
          }
        } else {
          // Handle non-response errors
          this.error = error.message || 'Unknown error'
          this.loadedSurvey = true
          this.survey = {
            title: '',
            description: '',
            options: {
              baseColor: '#2c2d4a',
              secondaryColor: '#90919e',
              highlightColor: '#00aef1',
              backgroundColor: '#ffffff'
            },
            mainImage: null
          }
        }
      })
    }
  },

  data() {
    return {
      locale: 'eng',
      loadedSurvey: false,
      surveyStatus: null,
      survey: null,
      formSubmitted: false,
      isSubmitting: false,
      groups: null,
      isGeneric: false,
      guest: {},
      anonymousGuest: null,
      pms_external_id: null,
      guestDetailsError: '',
      guestApiErrors: null,
      currentPage: 1,
      error: null
    }
  }
}
</script>
<style scoped>
/* Ensure error page container matches thank-you-message-container styling */
.error-page-container {
  display: flex;
  flex-direction: column;
  margin-bottom: 20px;
}

.error-page-container .bottom {
  padding: 2rem;
}

/* Card styling for error page */
#gf-survey .error-page-container .error-page {
  width: 90%;
  margin: 0 auto;
  box-shadow: 0 0 18px rgba(0, 0, 0, 0.14);
  border-radius: 25px;
  padding: 15px;
  min-height: 200px;
}
</style>
<style>
@import '../../node_modules/survey-fields/dist/css/build.css';
.submit-button {
  padding: 10px 15px;
  font-weight: bold;
  border: 0;
  color: #fff;
  background: #00aef1;
  cursor: pointer;
  min-width: 200px;
}
.submit-button:disabled {
  background: #ccc !important;
}
.button-wrapper {
  text-align: center;
  margin-top: 15px;
}
.submit-button:hover {
  box-shadow: 0 0 15px rgba(0, 0, 0, 0.15);
}
.bottom {
  margin-bottom: 20px;
}
.side-image {
  height: 100%;
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center center;
}
.survey-container {
  min-height: 100vh;
  border-radius: 0% !important;
}
.settings {
  display: flex;
  justify-content: flex-end;
}

.pagination-controls {
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-top: 15px;
}

.pagination-button {
  padding: 10px 15px;
  font-weight: bold;
  cursor: pointer;
  min-width: 100px;
}

.back-button {
  background-color: transparent;
  border: 1px solid;
}

.next-button {
  border: 0;
  color: #fff;
}

.next-button:hover,
.back-button:hover {
  box-shadow: 0 0 15px rgba(0, 0, 0, 0.15);
}

.pagination-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 15px;
  text-align: center;
}

.page-title {
  font-size: 1.3em;
  font-weight: bold;
  margin-bottom: 5px;
}

.page-indicator {
  font-size: 14px;
  margin: 5px 0;
  width: 100%;
  text-align: center;
  font-weight: 500;
}
</style>



// WEBPACK FOOTER //
// src/components/Survey.vue